import { useState, useEffect, useCallback } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import { Toggle } from '@/components/ui/Toggle'
import { WorkloadModal } from '@/components/ui/WorkloadModal'
import { Trash2, UserPlus, Users, Shield, Download, Archive, ArchiveRestore, ChevronRight, BarChart2 } from 'lucide-react'
import { Avatar } from '@/components/ui/Avatar'

const COLORS = ['#e63946', '#a78bfa', '#60a5fa', '#34d399', '#fb923c', '#facc15', '#f472b6', '#94a3b8']
const ICONS  = [
  // Shapes
  '◆', '◈', '✦', '◉', '★', '❋', '⬡', '▲', '●', '■',
  // People & faces
  '🎤', '🧑‍🎨', '👤', '🤝', '💼',
  // Creative & media
  '🎬', '📸', '🎨', '✏️', '🖼️', '📹', '🎵', '🎧', '📱', '💻',
  // Business & work
  '🏢', '📊', '📋', '📌', '🗂️', '📁', '🔖', '💡', '🚀', '⚡',
  // Nature & animals
  '🌿', '🌸', '🔥', '⭐', '🌙', '🌊', '🦋', '🐧', '🦖', '🐝',
  // Objects & symbols
  '💎', '🏆', '🎯', '🔑', '🧩', '🎁', '🛠️', '⚙️', '🌐', '📡',
]

const PERM_LABELS = [
  { key: 'create_tasks',    label: 'Create tasks' },
  { key: 'edit_tasks',      label: 'Edit tasks' },
  { key: 'delete_tasks',    label: 'Delete tasks' },
  { key: 'manage_sections', label: 'Manage sections' },
  { key: 'invite_members',  label: 'Invite members' },
  { key: 'approve_tasks',   label: 'Approve tasks' },
]


function StatCard({ label, value, accent }) {
  return (
    <div className="bg-surface-raised border border-border rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-text-faint">{label}</span>
      <span className={cn('text-xl font-bold tabular-nums', accent ? 'text-accent' : 'text-text')}>{value}</span>
    </div>
  )
}

export function ProjectSettingsModal({ projectId, open, onClose }) {
  const { projects, tasks, sections, getProjectMembers, updateProject, deleteProject,
          inviteToProject, updateMemberPermissions, updateMemberRole, removeMember, currentUser,
          archiveProject, unarchiveProject, exportTasksCSV, updateSection } = useAppStore()
  const navigate = useNavigate()
  const project = projects.find((p) => p.id === projectId)
  const isOwner = project?.ownerId === currentUser?.id || project?.role === 'owner'
  const isClientBoard = !!project?.parentId

  const SECTIONS = ['General', 'Members', ...(isOwner ? ['Permissions'] : []), ...(isClientBoard && isOwner ? ['Deliverables'] : []), 'Danger Zone']

  const DELIVERABLE_TYPES = [
    { value: '', label: 'None' },
    { value: 'reels', label: 'Reels' },
    { value: 'graphics', label: 'Graphics' },
    { value: 'photos', label: 'Photos' },
    { value: 'feed', label: 'Feed' },
  ]

  // General tab state
  const [section, setSection] = useState('General')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [color, setColor] = useState('#e63946')
  const [icon, setIcon] = useState('◈')
  const [privacy, setPrivacy] = useState('private')
  const [saved, setSaved] = useState(false)

  // Members tab state
  const [members, setMembers] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('member')
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState(null)
  const [inviteLink, setInviteLink] = useState(null)

  // Permissions tab state
  const [savingPerms, setSavingPerms] = useState({})
  const [confirmRemove, setConfirmRemove] = useState(null)
  const [highlightMemberId, setHighlightMemberId] = useState(null)

  // Workload view state
  const [workloadMemberId, setWorkloadMemberId] = useState(null)

  // Danger zone state
  const [deleteConfirm, setDeleteConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)

  const loadMembers = useCallback(async () => {
    if (!projectId) return
    const data = await getProjectMembers(projectId, { force: true })
    setMembers(data)
  }, [projectId, getProjectMembers])

  useEffect(() => {
    if (open && project) {
      setName(project.name || '')
      setDescription(project.description || '')
      setColor(project.color || '#e63946')
      setIcon(project.icon || '◈')
      setPrivacy(project.privacy || 'private')
      setSection('General')
      setDeleteConfirm('')
      setSaved(false)
      setInviteLink(null)
      setInviteEmail('')
      loadMembers()
    }
  }, [open, projectId])

  if (!project) return null

  const projectTasks = tasks.filter((t) => t.projectId === projectId)
  const completed = projectTasks.filter((t) => t.completed).length
  const open_ = projectTasks.length - completed
  const pct = projectTasks.length > 0 ? Math.round((completed / projectTasks.length) * 100) : 0
  const sectionCount = sections.filter((s) => s.projectId === projectId).length

  function handleSave() {
    updateProject(projectId, { name, description, color, icon, privacy })
    setSaved(true)
    setTimeout(() => setSaved(false), 1500)
  }

  async function handleInvite(e) {
    e.preventDefault()
    setInviteError(null)
    setInviting(true)
    const { error, inviteLink: link } = await inviteToProject(projectId, inviteEmail, inviteRole)
    setInviting(false)
    if (error) { setInviteError(error.message); return }
    setInviteLink(link)
    setInviteEmail('')
    loadMembers()
  }

  async function handlePermChange(member, key, val) {
    const newPerms = { ...member.permissions, [key]: val }
    setMembers((prev) => prev.map((m) => m.userId === member.userId ? { ...m, permissions: newPerms } : m))
    setSavingPerms((s) => ({ ...s, [member.userId]: true }))
    await updateMemberPermissions(projectId, member.userId, newPerms)
    setSavingPerms((s) => ({ ...s, [member.userId]: false }))
  }

  async function handleRemove(member) {
    if (confirmRemove !== member.userId) {
      setConfirmRemove(member.userId)
      setTimeout(() => setConfirmRemove(null), 3000)
      return
    }
    setConfirmRemove(null)
    setMembers((prev) => prev.filter((m) => m.userId !== member.userId))
    await removeMember(projectId, member.userId)
  }

  function goToPermissions(member) {
    setHighlightMemberId(member.userId)
    setSection('Permissions')
    setTimeout(() => {
      document.getElementById(`perm-${member.userId}`)?.scrollIntoView({ behavior: 'auto', block: 'nearest' })
    }, 80)
    setTimeout(() => setHighlightMemberId(null), 1800)
  }

  async function handleDelete() {
    if (deleteConfirm !== project.name) return
    setDeleting(true)
    deleteProject(projectId)
    onClose()
    navigate('/my-tasks')
  }

  return (
    <>
    <Modal open={open} onClose={onClose} title="Project settings" size="lg">
      <div className="flex gap-5 -mt-1">
        {/* Nav */}
        <div className="w-36 flex-shrink-0 space-y-0.5">
          {SECTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setSection(s)}
              className={cn(
                'w-full text-left px-3 h-8 rounded-lg text-sm transition-colors active:scale-[0.97]',
                section === s
                  ? s === 'Danger Zone' ? 'bg-red-500/10 text-red-400' : 'bg-accent/10 text-accent'
                  : s === 'Danger Zone' ? 'text-red-400/60 hover:text-red-400 hover:bg-red-500/10' : 'text-text-muted hover:text-text hover:bg-surface-raised'
              )}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-4 max-h-[60vh] overflow-y-auto pr-1">

          {/* ── General ── */}
          {section === 'General' && (
            <>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                  style={{ background: color + '20', color, border: `1px solid ${color}30` }}>
                  {icon}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-text truncate">{name || 'Untitled project'}</p>
                  <p className="text-xs text-text-faint">{privacy === 'private' ? 'Private' : 'Public'} · {sectionCount} section{sectionCount !== 1 ? 's' : ''}</p>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <StatCard label="Tasks" value={projectTasks.length} />
                <StatCard label="Done" value={completed} accent />
                <StatCard label="Open" value={open_} />
                <StatCard label="Members" value={members.length || '—'} />
              </div>

              {projectTasks.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px] text-text-faint">Completion</span>
                    <span className="text-[11px] font-semibold text-text-muted">{pct}%</span>
                  </div>
                  <div className="h-1.5 bg-surface-raised rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              )}

              <div className="border-t border-border/50" />

              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Project name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-1.5 block">Description</label>
                <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="What's this project about?" rows={2}
                  className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-text placeholder:text-text-faint focus:outline-none focus:ring-1 focus:ring-accent/40 resize-none" />
              </div>
              <div>
                <label className="text-xs text-text-muted mb-2 block">Color</label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setColor(c)}
                      className="w-7 h-7 rounded-full active:scale-[0.94] transition-transform"
                      style={{ background: c, outline: color === c ? `2px solid ${c}` : '2px solid transparent', outlineOffset: 2 }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-2 block">Icon</label>
                <div className="flex gap-1.5 flex-wrap">
                  {ICONS.map((ic) => (
                    <button key={ic} onClick={() => setIcon(ic)}
                      className={cn('w-8 h-8 rounded-lg text-base flex items-center justify-center transition-all active:scale-[0.94]',
                        icon === ic ? 'bg-accent/15 text-accent' : 'bg-surface-raised text-text-muted hover:text-text')}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted mb-2 block">Privacy</label>
                <div className="flex gap-2">
                  {[['private', 'Private', 'Only members can access'], ['public', 'Public', 'Anyone with the link']].map(([val, label, desc]) => (
                    <button key={val} onClick={() => setPrivacy(val)}
                      className={cn('flex-1 text-left px-3 py-2.5 rounded-xl border text-sm transition-all active:scale-[0.98]',
                        privacy === val ? 'border-accent/40 bg-accent/8 text-text' : 'border-border text-text-muted hover:text-text')}>
                      <p className="font-medium text-xs">{label}</p>
                      <p className="text-[11px] text-text-faint mt-0.5">{desc}</p>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t border-border">
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>{saved ? '✓ Saved' : 'Save changes'}</Button>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => exportTasksCSV(projectId)}
                    title="Export tasks as CSV"
                  >
                    <Download size={13} />
                    Export CSV
                  </Button>
                  {project.completed ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { unarchiveProject(projectId); onClose() }}
                    >
                      <ArchiveRestore size={13} />
                      Unarchive
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { archiveProject(projectId); onClose() }}
                    >
                      <Archive size={13} />
                      Archive
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Members ── */}
          {section === 'Members' && (
            <div className="space-y-4">
              {/* Invite form */}
              <form onSubmit={handleInvite} className="space-y-3">
                <label className="text-xs text-text-muted block">Invite by email</label>
                <div className="flex gap-2">
                  <Input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="teammate@company.co" required className="flex-1" />
                  <Button type="submit" variant="primary" disabled={inviting}>
                    {inviting ? 'Sending…' : 'Invite'}
                  </Button>
                </div>
                {inviteError && <p className="text-xs text-red-400">{inviteError}</p>}
                {inviteLink && (
                  <p className="text-xs text-text-muted">
                    ✓ Invite sent — <span className="font-mono text-text truncate">{inviteLink}</span>
                  </p>
                )}
              </form>

              <div className="border-t border-border/50" />

              {/* Members list */}
              {members.length === 0 ? (
                <div className="text-center py-6">
                  <Users size={18} className="text-text-faint mx-auto mb-2" />
                  <p className="text-sm text-text-muted">No members yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {members.map((m) => {
                    const canEditPerms = isOwner && m.userId !== currentUser?.id
                    const canChangeRole = isOwner && m.userId !== currentUser?.id
                    return (
                      <div key={m.userId} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface-raised group">
                        <Avatar user={m} size="md" />
                        <div
                          className={cn('flex-1 min-w-0', canEditPerms && 'cursor-pointer')}
                          onClick={canEditPerms ? () => goToPermissions(m) : undefined}
                          title={canEditPerms ? 'Edit permissions' : undefined}
                        >
                          <div className="flex items-center gap-1">
                            <p className={cn(
                              'text-sm font-medium truncate',
                              canEditPerms ? 'text-text group-hover:text-accent transition-colors' : 'text-text'
                            )}>{m.name}</p>
                            {canEditPerms && (
                              <ChevronRight size={12} className="text-text-faint opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-xs text-text-faint truncate">{m.email}</p>
                        </div>
                        {canChangeRole ? (
                          <select
                            value={m.role}
                            onChange={async (e) => {
                              const newRole = e.target.value
                              const prevRole = m.role
                              setMembers((prev) => prev.map((x) => x.userId === m.userId ? { ...x, role: newRole } : x))
                              const result = await updateMemberRole(projectId, m.userId, newRole)
                              if (!result?.ok) {
                                setMembers((prev) => prev.map((x) => x.userId === m.userId ? { ...x, role: prevRole } : x))
                                return
                              }
                              loadMembers()
                            }}
                            className="text-[10px] font-semibold uppercase tracking-wider text-text-faint px-2 py-0.5 rounded-full bg-surface border border-border cursor-pointer hover:border-accent/40 transition-colors appearance-none pr-5"
                            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3l3 3 3-3' stroke='%23888' fill='none' stroke-width='1.2'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                          >
                            <option value="owner">Owner</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        ) : (
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-faint px-2 py-0.5 rounded-full bg-surface border border-border">
                            {m.role}
                          </span>
                        )}
                        <button
                          onClick={() => setWorkloadMemberId(m.userId)}
                          className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg text-text-faint hover:text-accent hover:bg-accent/10 transition-all active:scale-[0.97]"
                          title="View workload"
                        >
                          <BarChart2 size={13} />
                        </button>
                        {isOwner && m.userId !== currentUser?.id && (
                          <button
                            onClick={() => handleRemove(m)}
                            style={{ opacity: confirmRemove === m.userId ? 1 : undefined }}
                            className={`opacity-0 group-hover:opacity-100 flex items-center justify-center rounded-lg transition-all active:scale-[0.97] ${
                              confirmRemove === m.userId
                                ? 'h-7 px-2 text-[10px] font-semibold text-red-400 bg-red-400/15 border border-red-400/30'
                                : 'w-7 h-7 text-text-faint hover:text-red-400 hover:bg-red-400/10'
                            }`}
                            title={confirmRemove === m.userId ? 'Click again to confirm' : 'Remove member'}
                          >
                            {confirmRemove === m.userId ? 'confirm?' : <Trash2 size={13} />}
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* ── Permissions (owner only) ── */}
          {section === 'Permissions' && isOwner && (
            <div className="space-y-5">
              {members.filter((m) => m.role !== 'owner').length === 0 ? (
                <div className="text-center py-6">
                  <Shield size={18} className="text-text-faint mx-auto mb-2" />
                  <p className="text-sm text-text-muted">No members to configure</p>
                  <p className="text-xs text-text-faint mt-1">Invite someone first</p>
                </div>
              ) : (
                members.filter((m) => m.role !== 'owner').map((member) => (
                  <div key={member.userId} id={`perm-${member.userId}`}>
                    <div className={cn(
                      'flex items-center gap-2.5 mb-3 rounded-lg px-2 py-1 -mx-2 transition-all duration-300',
                      highlightMemberId === member.userId && 'bg-accent/10 ring-1 ring-accent/30'
                    )}>
                      <Avatar user={member} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-text truncate">{member.name}</p>
                        <p className="text-[11px] text-text-faint truncate">{member.email}</p>
                      </div>
                      {savingPerms[member.userId] && <span className="text-[10px] text-text-faint">Saving…</span>}
                    </div>
                    <div className="space-y-3 pl-8">
                      {PERM_LABELS.map(({ key, label }) => (
                        <div key={key} className="flex items-center justify-between gap-4">
                          <span className="text-xs text-text-muted">{label}</span>
                          <Toggle
                            on={member.permissions?.[key] !== false}
                            onChange={(val) => handlePermChange(member, key, val)}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 border-t border-border/50" />
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Deliverables ── */}
          {section === 'Deliverables' && (
            <div className="space-y-2">
              <p className="text-xs text-text-faint pb-1">Set the deliverable type and monthly quota for each section. These drive the reporting dashboard.</p>
              {sections.filter((s) => s.projectId === projectId && s.type !== 'approval' && s.type !== 'complete').map((s) => (
                <div key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-surface-raised">
                  <span className="text-sm text-text flex-1 min-w-0 truncate">{s.name}</span>
                  <select
                    value={s.deliverableType || ''}
                    onChange={(e) => updateSection(s.id, { deliverableType: e.target.value || null })}
                    className="text-xs text-text-muted bg-surface border border-border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-accent/40 appearance-none pr-6"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' viewBox='0 0 8 8' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3l3 3 3-3' stroke='%23888' fill='none' stroke-width='1.2'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 6px center' }}
                  >
                    {DELIVERABLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input
                    type="number"
                    min="0"
                    placeholder="Quota"
                    value={s.deliverableQuota || ''}
                    onChange={(e) => updateSection(s.id, { deliverableQuota: e.target.value ? Number(e.target.value) : null })}
                    className="w-20 text-xs text-text bg-surface border border-border rounded-lg px-2 py-1.5 text-center focus:outline-none focus:ring-1 focus:ring-accent/40 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── Danger Zone ── */}
          {section === 'Danger Zone' && (
            <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/5 space-y-3">
              <div className="flex items-start gap-3">
                <Trash2 size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-text">Delete this project</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    Permanently deletes <strong>{project.name}</strong> and all its tasks and sections. Cannot be undone.
                  </p>
                </div>
              </div>
              {isOwner ? (
                <>
                  <div>
                    <label className="text-xs text-text-muted mb-1.5 block">
                      Type <span className="font-mono text-text">{project.name}</span> to confirm
                    </label>
                    <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder={project.name} />
                  </div>
                  <Button variant="ghost" onClick={handleDelete}
                    disabled={deleteConfirm !== project.name || deleting}
                    className="w-full border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-40">
                    {deleting ? 'Deleting…' : 'Delete project'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-text-faint mb-3">Only the project owner can delete this project.</p>
                  <Button variant="ghost" onClick={async () => {
                    if (!window.confirm(`Leave ${project.name}? You'll need to be re-invited to rejoin.`)) return
                    await removeMember(projectId, currentUser.id)
                    onClose()
                    navigate('/home')
                  }}
                    className="w-full border border-red-500/30 text-red-400 hover:bg-red-500/10">
                    Leave project
                  </Button>
                </>
              )}
            </div>
          )}

        </div>
      </div>
    </Modal>

    {workloadMemberId && (
      <WorkloadModal
        projectId={projectId}
        initialMemberId={workloadMemberId}
        onClose={() => setWorkloadMemberId(null)}
      />
    )}
    </>
  )
}
